/**
 * TasksCard - the Dashboard's "Project tasks": what's outstanding on WON
 * projects (PM work), defaulting to MY tasks with a toggle for everyone's.
 *
 * Paul Q10: one task list for leads and opportunities (the "My tasks" card),
 * while tasks on won projects stay a separate list - this one. Both read
 * crm_actions; a task on a row that is not a won project (isProjectRow)
 * belongs to My tasks and is left out here.
 *
 * My tasks counts a task I own OR am tagged on (10/6/26); one I am on only
 * because I was tagged says "Tagged" and names its owner.
 *
 * A re-read that fails (a 429 or 502 while every open tab re-reads after
 * someone's change) keeps the list as it is; only a read the person is
 * waiting for - the first, My / All, Try again - says it failed.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fmtDate } from '../../utils/format';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { indexById, taskKind } from '../crm/nextStep';
import { isOverdue, useTasksChanged } from './useTasks';
import { useCompleteTask } from './useCompleteTask';
import { latestReads } from '../../live/useLive';
import { TaggedChip, TaggedNames } from './TagPeople';
import { taggedNotOwner, taggedOf } from './tagged';

const GREEN = '#1B5E20';
const AMBER = '#B07503';

/* `fill`: stretch to the height of the box it is placed in (the Dashboard's
   Project tasks widget), and let the list take whatever is left and scroll
   (.dash-w-body .tasks-list in index.css). */
export default function TasksCard({ onOpen, fill = false }) {
  const { allProjects, loading, users } = useApp();
  const { user } = useAuth();
  const [scope, setScope] = useState('mine'); // 'mine' | 'all'
  // { scope, rows } once read: each list remembers which toggle it answers,
  // so My tasks never show under All tasks while that loads (or could not)
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(''); // the toggle whose read, one the person waited for, failed
  // "Loading…" only until the first load lands - every AppContext refresh flips loading again
  const [loadedOnce, setLoadedOnce] = useState(!loading);
  if (!loading && !loadedOnce) setLoadedOnce(true);

  const [reads] = useState(latestReads); // reads can overlap: the newest answer GOT wins
  const load = useCallback(
    ({ live } = {}) => {
      const ticket = reads.start();
      // no entity_type filter: a legacy 'opportunity' task on a row that has
      // since been won is a project task too (taskKind sorts them out)
      const q = `status=open${scope === 'mine' ? '&owner=me' : ''}`;
      fetch(`/api/crm-actions?${q}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!Array.isArray(d)) throw new Error('not a list');
          if (reads.show(ticket)) {
            setItems({ scope, rows: d });
            setFailed((f) => (f === scope ? '' : f));
          }
        })
        // the list stays as it is; someone else's change (live) re-reads quietly
        .catch(() => {
          if (!live && reads.show(ticket)) setFailed(scope);
        });
    },
    [scope, reads]
  );
  useEffect(load, [load]);
  useTasksChanged(load);
  // same completion as everywhere else; a won project never asks for a next step
  const { complete, prompt } = useCompleteTask();

  const projectsById = useMemo(() => indexById(allProjects), [allProjects]);
  const current = !!items && items.scope === scope;
  const rows = useMemo(
    () => (current ? items.rows.filter((t) => taskKind(t, projectsById) === 'project') : []),
    [current, items, projectsById]
  );
  // wait for the project list too, or every task would look like an opportunity's for a moment
  const loaded = current && (loadedOnce || !loading);
  const overdue = rows.filter(isOverdue).length;

  return (
    // fontFamily inherit: 'Montserrat' is named across the app but never
    // loaded, and with no fallback this card was rendering in Times.
    <div
      style={{
        background: '#fff',
        borderRadius: 8,
        border: '1px solid #E8E8E8',
        padding: 20,
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'inherit',
        // fill: the Dashboard widget's card - the same corners and edge as its neighbours
        ...(fill
          ? {
              height: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 12,
              border: '1px solid #ECECEC',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              padding: '14px 16px 12px',
            }
          : null),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#5A5A5A', letterSpacing: 1.5, textTransform: 'uppercase' }}>
            Project tasks
          </div>
          {!loaded && failed === scope ? (
            <div role="alert" style={{ fontSize: 11, color: '#B71C1C', fontWeight: 700, marginTop: 3 }}>
              Could not load the tasks.{' '}
              <button
                type="button"
                onClick={() => {
                  setFailed('');
                  load();
                }}
                style={{
                  padding: 0,
                  fontSize: 11,
                  fontWeight: 700,
                  color: GREEN,
                  fontFamily: 'inherit',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Try again
              </button>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: '#6B6B6B', marginTop: 3 }}>
              {loaded ? `${rows.length} open${overdue ? ` · ${overdue} overdue` : ''}` : 'Loading…'}
            </div>
          )}
        </div>
        <div style={{ display: 'inline-flex', borderRadius: 7, border: '1px solid #D8DED4', overflow: 'hidden' }}>
          {[
            ['mine', 'My tasks'],
            ['all', 'All tasks'],
          ].map(([k, lbl]) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                setFailed('');
                setScope(k);
              }}
              style={{
                padding: '5px 12px',
                border: 'none',
                cursor: 'pointer',
                fontSize: 10.5,
                fontWeight: 700,
                fontFamily: 'inherit',
                background: scope === k ? GREEN : '#fff',
                color: scope === k ? '#fff' : '#555',
              }}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {loaded && rows.length === 0 && (
        <div style={{ padding: '16px 4px', color: '#9AA29B', fontSize: 11.5 }}>
          {scope === 'mine' ? 'Nothing assigned to you on won projects. ' : 'No open project tasks. '}
          Right-click any row on Projects to add one.
        </div>
      )}

      <div
        className="tasks-list"
        style={{ display: 'flex', flexDirection: 'column', gap: 7, overflowY: 'auto', ...(fill ? null : { maxHeight: 260 }) }}
      >
        {loaded &&
          rows.map((t) => {
            const late = isOverdue(t);
            const taggedMe = taggedNotOwner(t, user, users);
            return (
              <div key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                <button
                  type="button"
                  title="Mark done"
                  onClick={() => complete(t)}
                  style={{
                    flexShrink: 0,
                    marginTop: 2,
                    width: 15,
                    height: 15,
                    borderRadius: 4,
                    cursor: 'pointer',
                    border: '1.5px solid #BBB',
                    background: '#fff',
                    padding: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <button
                    type="button"
                    onClick={() => onOpen && onOpen(t)}
                    style={{
                      display: 'block',
                      textAlign: 'left',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: onOpen ? 'pointer' : 'default',
                      fontSize: 11.5,
                      color: '#0D0D0D',
                      lineHeight: 1.4,
                      fontFamily: 'inherit',
                      wordBreak: 'break-word',
                      width: '100%',
                    }}
                  >
                    {t.description}
                  </button>
                  <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', fontSize: 9.5, marginTop: 2 }}>
                    {t.entity_label && <span style={{ color: '#6B6B6B' }}>{t.entity_label}</span>}
                    {taggedMe && <TaggedChip />}
                    {(scope === 'all' || taggedMe) && <span style={{ color: GREEN, fontWeight: 700 }}>{t.owner}</span>}
                    {scope === 'all' && <TaggedNames names={taggedOf(t)} />}
                    <span style={{ color: late ? AMBER : '#6B6B6B', fontWeight: late ? 700 : 400 }}>
                      due {fmtDate(t.due_date)}
                      {late ? ' · overdue' : ''}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
      {prompt}
    </div>
  );
}
